/**
 * Email validation ported from validator.js
 * Sources:
 *   https://cdn.jsdelivr.net/npm/validator@latest/lib/isEmail.js
 *   https://cdn.jsdelivr.net/npm/validator@latest/lib/isFQDN.js
 */

// Matches valid characters in each dot-separated segment of the local part (UTF-8 allowed)
const emailUserUtf8Part = /^[a-z\d!#$%&'*+\-/=?^_`{|}~\u00A1-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+$/i;

// Matches valid characters inside a quoted local part e.g. "user name"@example.com
const quotedEmailUserUtf8 =
  /^([\s\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|(\\[\x01-\x09\x0b\x0c\x0d-\x7f\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))*$/i;

/**
 * Validate an email address.
 *
 * Logic ported directly from validator.js (isEmail + isFQDN):
 *  - RFC 5321 max lengths: 254 total, 64 local part, 63 per domain label
 *  - TLD must be 2+ letters or a punycode label (xn--)
 *  - All-numeric TLDs are rejected
 *  - No full-width unicode characters in domain
 *  - Domain labels cannot start or end with a hyphen
 *  - Local part: each dot-separated segment validated against RFC-allowed chars (UTF-8)
 *  - Quoted local parts ("user name"@example.com) are supported
 */
export const isValidEmail = (email: string): boolean => {
  if (typeof email !== 'string' || email.length > 254) return false;

  const atIdx = email.lastIndexOf('@');
  if (atIdx <= 0) return false;

  const user = email.slice(0, atIdx);
  const domain = email.slice(atIdx + 1);

  // RFC 5321 length limits
  if (user.length === 0 || user.length > 64) return false;
  if (domain.length === 0 || domain.length > 254) return false;

  // Domain validation (isFQDN.js logic)
  const domainParts = domain.split('.');
  if (domainParts.length < 2) return false;

  const tld = domainParts[domainParts.length - 1];

  // TLD must be 2+ letters or a valid punycode label (xn followed by alphanumeric+hyphen)
  if (!/^([a-z\u00A1-\u00A8\u00AA-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]{2,}|xn[a-z0-9-]{2,})$/i.test(tld)) return false;

  // Reject all-numeric TLDs (e.g. user@example.123)
  if (/^\d+$/.test(tld)) return false;

  const domainValid = domainParts.every((part) => {
    if (part.length === 0 || part.length > 63) return false;
    if (!/^[a-z\u00a1-\uffff0-9-]+$/i.test(part)) return false;
    if (/[\uff01-\uff5e]/.test(part)) return false; // no full-width chars
    if (/^-|-$/.test(part)) return false;           // no leading/trailing hyphen
    return true;
  });
  if (!domainValid) return false;

  // Local part validation (isEmail.js logic)
  // Quoted local part: "user name"@example.com
  if (user[0] === '"' && user[user.length - 1] === '"') {
    return quotedEmailUserUtf8.test(user.slice(1, -1));
  }

  // Standard: each dot-separated segment must match RFC-allowed chars
  return user.split('.').every((part) => emailUserUtf8Part.test(part));
};
